import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { fetchRecommendations } from "../lib/recommendations";
import ProductCard from "../components/ProductCard";
import CatalogFilterPanel, { EMPTY_FILTERS } from "../components/CatalogFilterPanel";
import { SlidersHorizontal, X, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 50;

export default function CategoryPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
    occasion: searchParams.get("ocasiao") || "",
    minPrice: searchParams.get("min_price") || "",
    maxPrice: searchParams.get("max_price") || "",
  });
  const [sort, setSort] = useState("recent");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const gridTopRef = useRef(null);

  const isAll = slug === "todos";
  const categoryLabel = isAll ? "Todas as peças" : decodeURIComponent(slug);

  // Reset to page 1 whenever the active filter set or sort changes — without
  // this, a visitor on page 7 of "Anéis" who then selects "Brincos" would land
  // on page 7 of brincos, which is jarring.
  useEffect(() => {
    setPage(1);
  }, [slug, sort, filters]);

  // Sync filters with URL search params on every URL change
  // (so clicking different /presentes links updates an already-mounted CategoryPage).
  useEffect(() => {
    const nextOccasion = searchParams.get("ocasiao") || "";
    const nextMin = searchParams.get("min_price") || "";
    const nextMax = searchParams.get("max_price") || "";
    setFilters((prev) => {
      if (
        prev.occasion === nextOccasion &&
        prev.minPrice === nextMin &&
        prev.maxPrice === nextMax
      ) {
        return prev;
      }
      return { ...prev, occasion: nextOccasion, minPrice: nextMin, maxPrice: nextMax };
    });
  }, [searchParams]);

  // Session-scoped shuffle seed — generated ONCE when the component mounts,
  // then held stable through all category / filter / page changes within
  // this session. This guarantees:
  //   • No sort-by-price surprise: the seed always flows to the server, and
  //     the server default (round-robin + Fisher-Yates) is what the customer
  //     actually sees. NEVER use `.sort(() => Math.random() - 0.5)` — it
  //     produces a biased distribution and, worse, non-deterministic pages.
  //   • Pagination stability: same seed on page 1 → page 2 → page 3, so a
  //     product never repeats when the shopper flips forward.
  //   • Fresh browse next session: reload = new Date.now() = fresh mix.
  const [shuffleSeed] = useState(() => Date.now() % 2_147_483_647);

  // Build the server query params from current state. ALL filter facets now
  // run server-side so total_pages reflects the actual filtered count.
  const serverParams = useMemo(() => {
    const p = { page, limit: PAGE_SIZE, sort, seed: shuffleSeed };
    if (!isAll) p.category = decodeURIComponent(slug);
    else if (filters.category) p.category = filters.category;
    if (filters.brand) p.brand = filters.brand;
    if (filters.style) p.style = filters.style;
    if (filters.occasion) p.occasion = filters.occasion;
    if (filters.forWhom) p.for_whom = filters.forWhom;
    if (filters.combines) p.combines = filters.combines;
    if (filters.sophMin > 1) p.sophistication_min = filters.sophMin;
    if (filters.sophMax < 5) p.sophistication_max = filters.sophMax;
    if (filters.minPrice) p.min_price = filters.minPrice;
    if (filters.maxPrice) p.max_price = filters.maxPrice;
    return p;
  }, [slug, isAll, sort, page, filters, shuffleSeed]);

  // No-filter check — affinity recommendations are only spliced onto page 1
  // and only when the visitor hasn't narrowed by any facet (matches the
  // previous behaviour, just now scoped to a single page worth of items).
  const hasFacetFilters = !!(
    filters.style || filters.occasion || filters.forWhom || filters.combines ||
    filters.sophMin > 1 || filters.sophMax < 5 || filters.minPrice || filters.maxPrice
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/products", { params: serverParams }).then(async (r) => {
      if (cancelled) return;
      let items = r.data.items || [];
      const respTotal = r.data.total ?? items.length;
      const respPages = r.data.total_pages ?? 1;

      // Affinity injection — only on page 1, only when no facet filters,
      // only on default sort. Keeps the curated picks at the top of the first
      // screen the shopper sees.
      if (page === 1 && !hasFacetFilters && sort === "recent") {
        try {
          const { items: aff, source } = await fetchRecommendations({
            context: "catalog",
            limit: 12,
            localPool: () => items,
          });
          if (source === "affinity" && aff.length) {
            const headIds = new Set(aff.map((p) => p.id));
            const tail = items.filter((p) => !headIds.has(p.id));
            items = [...aff, ...tail].slice(0, PAGE_SIZE);
          }
        } catch { /* keep server order */ }
      }

      if (cancelled) return;
      setProducts(items);
      setTotal(respTotal);
      setTotalPages(respPages);
      // Server clamps page to total_pages — sync back so a stale URL doesn't desync the footer.
      if (r.data.page && r.data.page !== page) setPage(r.data.page);
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setProducts([]);
      setTotal(0);
      setTotalPages(1);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [serverParams, page, hasFacetFilters, sort]);

  const clearFilters = () => setFilters({ ...EMPTY_FILTERS });

  const goToPage = (next) => {
    const clamped = Math.max(1, Math.min(totalPages, next));
    if (clamped === page) return;
    setPage(clamped);
    if (gridTopRef.current) {
      gridTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <div data-testid="category-page">
      <div className="bg-[#FAF9F7] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="overline text-[#A8A8A8] mb-4">Catálogo</p>
          <h1 className="font-display text-5xl md:text-6xl">{categoryLabel}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8" ref={gridTopRef}>
          <p className="text-sm text-[#6B6B6B]" data-testid="catalog-count">
            {loading
              ? "…"
              : total === 0
                ? "0 peças"
                : `Exibindo ${rangeStart}–${rangeEnd} de ${total} ${total === 1 ? "peça" : "peças"}`}
          </p>
          <div className="flex items-center gap-4">
            <button
              data-testid="open-mobile-filters"
              className="md:hidden inline-flex items-center gap-2 overline"
              onClick={() => setMobileFiltersOpen(true)}
            >
              <SlidersHorizontal size={14} /> Filtros
            </button>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="overline border-b border-[#E8E4DF] py-1 bg-transparent focus:outline-none focus:border-[#B8A88A]"
            >
              <option value="recent">Mais recente</option>
              <option value="price_asc">Menor preço</option>
              <option value="price_desc">Maior preço</option>
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-[240px_1fr] gap-10">
          <aside className="hidden md:block">
            <CatalogFilterPanel filters={filters} setFilters={setFilters} showCategory={isAll} onClear={clearFilters} />
          </aside>
          <div>
            {loading ? (
              <p className="text-[#A8A8A8] italic">Carregando peças...</p>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <p className="font-display text-2xl mb-2">Nenhuma peça encontrada</p>
                <p className="text-[#6B6B6B] text-sm">Tente ajustar os filtros.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
                  {products.slice(0, 6).map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
                {products.length > 6 && (
                  <div className="my-12 py-8 bg-[#FAF9F7] text-center px-6">
                    <p className="font-italic-display text-lg text-[#8C7B5E]">
                      Cada joia em nosso catálogo é única.<br/>
                      Uma vez vendida, não estará mais disponível.
                    </p>
                  </div>
                )}
                {products.length > 6 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
                    {products.slice(6).map((p) => <ProductCard key={p.id} product={p} />)}
                  </div>
                )}

                {/* Pagination footer — discreet, matching the catalogue tone */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-14 pt-8 border-t border-[#E8E4DF]" data-testid="catalog-pagination">
                    <button
                      type="button"
                      onClick={() => goToPage(page - 1)}
                      disabled={page <= 1 || loading}
                      data-testid="catalog-pagination-prev"
                      className="inline-flex items-center gap-1.5 overline text-[#6B6B6B] hover:text-[#1A1A1A] disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={14}/> Anterior
                    </button>
                    <p className="overline text-[#A8A8A8]" data-testid="catalog-pagination-status">
                      Página <span className="text-[#1A1A1A]">{page}</span> de <span className="text-[#1A1A1A]">{totalPages}</span>
                    </p>
                    <button
                      type="button"
                      onClick={() => goToPage(page + 1)}
                      disabled={page >= totalPages || loading}
                      data-testid="catalog-pagination-next"
                      className="inline-flex items-center gap-1.5 overline text-[#6B6B6B] hover:text-[#1A1A1A] disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Próxima <ChevronRight size={14}/>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filters bottom sheet */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 bg-black/40 z-[100] md:hidden flex items-end" onClick={() => setMobileFiltersOpen(false)}>
          <div className="bg-white w-full max-h-[80vh] overflow-y-auto rounded-t-xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl">Filtros</h3>
              <button onClick={() => setMobileFiltersOpen(false)} data-testid="close-mobile-filters">
                <X size={20} />
              </button>
            </div>
            <CatalogFilterPanel filters={filters} setFilters={setFilters} showCategory={isAll} onClear={clearFilters} />
            <button className="btn-primary w-full mt-8" onClick={() => setMobileFiltersOpen(false)}>
              Aplicar filtros
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
